import {useEffect, useState} from 'react'

function rectContainsY(rect, y) {
  return y >= rect.top && y <= rect.bottom
}

function rectsOverlap(a, b) {
  return a.bottom >= b.top && a.top <= b.bottom
}

function overlapsDarkSection({targetRect, darkSections, mode}) {
  const targetProbeY = targetRect.top + targetRect.height / 2

  return Array.from(darkSections).some(section => {
    const sectionRect = section.getBoundingClientRect()

    if (mode === 'any') {
      return rectsOverlap(targetRect, sectionRect)
    }

    return rectContainsY(sectionRect, targetProbeY)
  })
}

function setOverlayClip(target, darkSections) {
  const targetRect = target.getBoundingClientRect()
  const darkSection = Array.from(darkSections).find(section =>
    rectsOverlap(targetRect, section.getBoundingClientRect()),
  )

  if (!darkSection) {
    target.style.setProperty('--dark-overlay-top', `${targetRect.height}px`)
    target.style.setProperty('--dark-overlay-bottom', '0px')
    return
  }

  const darkRect = darkSection.getBoundingClientRect()
  const top = Math.min(
    targetRect.height,
    Math.max(0, darkRect.top - targetRect.top),
  )
  const bottom = Math.min(
    targetRect.height,
    Math.max(0, targetRect.bottom - darkRect.bottom),
  )

  target.style.setProperty('--dark-overlay-top', `${top}px`)
  target.style.setProperty('--dark-overlay-bottom', `${bottom}px`)
}

function clearOverlayClip(targetSelector) {
  if (!targetSelector) return

  const targets = document.querySelectorAll(targetSelector)
  targets.forEach(target => {
    target.style.removeProperty('--dark-overlay-top')
    target.style.removeProperty('--dark-overlay-bottom')
  })
}

/**
 * @param {object} [opts]
 * @param {string} [opts.targetSelector]
 * @param {string} [opts.darkSectionSelector]
 * @param {boolean} [opts.disabled]
 * @param {HTMLElement | null} [opts.containerEl]
 */
export function useDarkSectionSplitOverlay({
  targetSelector,
  darkSectionSelector,
  disabled = false,
  containerEl = null,
} = {}) {
  useEffect(() => {
    if (disabled || !targetSelector || !darkSectionSelector) {
      clearOverlayClip(targetSelector)
      return
    }

    let frame = null

    const update = () => {
      frame = null

      const targets = document.querySelectorAll(targetSelector)
      const darkSections = document.querySelectorAll(darkSectionSelector)

      targets.forEach(target => setOverlayClip(target, darkSections))
    }

    const requestUpdate = () => {
      if (frame !== null) return
      frame = window.requestAnimationFrame(update)
    }

    requestUpdate()
    const scrollOptions = {passive: true, capture: true}
    const scrollTarget = containerEl || window

    scrollTarget.addEventListener('scroll', requestUpdate, scrollOptions)
    window.addEventListener('resize', requestUpdate)

    return () => {
      if (frame !== null) {
        window.cancelAnimationFrame(frame)
      }
      scrollTarget.removeEventListener('scroll', requestUpdate, scrollOptions)
      window.removeEventListener('resize', requestUpdate)
      clearOverlayClip(targetSelector)
    }
  }, [targetSelector, darkSectionSelector, disabled, containerEl])
}

function useElementOverDarkSection({
  targetSelector,
  darkSectionSelector = '[data-dark-section="true"]',
  mode = 'midpoint',
  disabled = false,
} = {}) {
  const [isOverDarkSection, setIsOverDarkSection] = useState(false)

  useEffect(() => {
    if (disabled || !targetSelector) {
      // Reset stale measurements when the selector is removed or intentionally disabled.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsOverDarkSection(current => (current ? false : current))
      return
    }

    let frame = null

    const update = () => {
      frame = null

      const target = document.querySelector(targetSelector)
      const darkSections = document.querySelectorAll(darkSectionSelector)

      if (!target || darkSections.length === 0) {
        setIsOverDarkSection(false)
        return
      }

      const targetRect = target.getBoundingClientRect()
      const nextIsOverDarkSection = overlapsDarkSection({
        targetRect,
        darkSections,
        mode,
      })

      setIsOverDarkSection(current =>
        current === nextIsOverDarkSection ? current : nextIsOverDarkSection,
      )
    }

    const requestUpdate = () => {
      if (frame !== null) return
      frame = window.requestAnimationFrame(update)
    }

    requestUpdate()
    const scrollOptions = {passive: true, capture: true}

    window.addEventListener('scroll', requestUpdate, scrollOptions)
    window.addEventListener('resize', requestUpdate)

    return () => {
      if (frame !== null) {
        window.cancelAnimationFrame(frame)
      }
      window.removeEventListener('scroll', requestUpdate, scrollOptions)
      window.removeEventListener('resize', requestUpdate)
    }
  }, [targetSelector, darkSectionSelector, mode, disabled])

  return disabled || !targetSelector ? false : isOverDarkSection
}

export default useElementOverDarkSection
